import { useCallback, useEffect, useState } from 'react'
import { settingsRepository } from '@/data/settings/repositories/settings_repository_impl'
import { getAxiosLikeErrorMessage } from '@/services/apiErrors'
import { logError } from '@/utils/logger'
import type { AboutFormErrors } from '../models/settings_ui_state'

export function useAboutContentViewModel() {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [errors, setErrors] = useState<AboutFormErrors>({})
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setIsLoading(true)
      try {
        const data = await settingsRepository.getAboutContent()
        if (!cancelled) {
          setTitle(data.title)
          setDescription(data.description)
        }
      } catch (error) {
        logError('Failed to load About Us content:', error)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setIsSubmitting(true)

      const newErrors: AboutFormErrors = {}
      if (!title.trim()) newErrors.title = 'Title is required'
      if (!description.trim()) newErrors.description = 'Description is required'

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors)
        setIsSubmitting(false)
        return
      }

      try {
        await settingsRepository.saveAboutContent({
          title: title.trim(),
          description: description.trim(),
        })
        alert('About Us content saved successfully!')
      } catch (error: unknown) {
        logError('Failed to save About Us:', error)
        alert(getAxiosLikeErrorMessage(error, 'Failed to save About Us content. Please try again.'))
      } finally {
        setIsSubmitting(false)
      }
    },
    [description, title]
  )

  const handleCancel = useCallback(() => {
    setTitle('')
    setDescription('')
    setErrors({})
  }, [])

  return {
    title,
    description,
    errors,
    isLoading,
    isSubmitting,
    setTitle,
    setDescription,
    setErrors,
    handleSubmit,
    handleCancel,
  }
}
